import { autoinject } from 'aurelia-framework';
import { ApiService } from '../../utils/api-service';
import {
	ReportTemplate,
	IReportTemplateAM,
	IComparisonType,
	IReportTemplateContent,
	ReportFinancialPrecision
} from './models/report-template';
import { json } from 'aurelia-fetch-client';
import { mapKeys, upperFirst, Dictionary } from 'lodash';
import { IDraggableHandler } from '../../components/drag-and-drop/models';

@autoinject
export class ReportService {
	constructor(private api: ApiService) {
	}

	async getReport(reportTemplateId: string, customerId: string): Promise<IReportAM> {
		return await this.api.fetchJson(`reports/${reportTemplateId}/for-customer/${customerId}`)
			.then((report: IReportAM) => {
				report.reportTemplateId = reportTemplateId;
				return report;
			});
	}

	async getReportForCustomer(reportTemplateId: string, customerId: string, includeDeletedRows: boolean, errorHandler?: (error: any) => void): Promise<IReportAM> {
		return await this.api
			.fetchJson(
				`reports/${reportTemplateId}/for-customer/${customerId}?includeDeletedRows=${includeDeletedRows}`,
				null,
				errorHandler
			)
			.then((report: IReportAM) => {
				if (report != null) {
					report.reportTemplateId = reportTemplateId;
					return report;
				}
			});
	}

	async getCalculatedReportForTemplate(
		customerId: string,
		templateContent: IReportTemplateContent): Promise<IReportAM> {

		return await this.api.fetchJson(`reports/calculate-for-template/for-customer/${customerId}`, {
			method: 'post',
			body: json(JSON.stringify(templateContent))
		});
	}

	async getReportTemplate(reportTemplateId: string): Promise<IReportTemplateAM> {
		const template = await this.api.fetchJson(`reports/${reportTemplateId}/template`);
		template.content = JSON.parse(template.content);
		return template as IReportTemplateAM;
	}

	async getReportLinks(customerId: string): Promise<IReportLinkResultAM> {
		const apiResult = await this.api.fetchJson(`reports/links/for-customer/${customerId}`) as IReportLinkResultAM;

		const result = {commonLinks: [], privateLinks: []};

		result.commonLinks = apiResult.commonLinks.map(l => Object.assign(new ReportLinkAM, l));
		result.privateLinks = apiResult.privateLinks.map(l => Object.assign(new ReportLinkAM, l));

		return result;
	}

	getReportPrintUriForTemplate(reportTemplateId: string, customerId: string): string {
		return `/reports/${reportTemplateId}/for-customer/${customerId}/print`;
	}

	addReportTemplate(customerId: string, template: ReportTemplate) {
		const templateAM = template.toAM();
		return this.api.fetch(`/reports/template/add/for-customer/${customerId}`, {
			method: 'post',
			body: json(templateAM)
		});
	}

	updateReportTemplateName(reportTemplateId: string, name: string) {
		return this.api.fetch(`/reports/${reportTemplateId}/template/update-name/${encodeURIComponent(name)}`, {
			method: 'post'
		});
	}

	updateReportTemplate(template: ReportTemplate) {
		const templateAM = template.toAM();

		return this.api.fetch(`/reports/${template.id}/template/update`, {
			method: 'post',
			body: json(templateAM)
		});
	}

	async deleteReportTemplate(template: IReportTemplateAM) {
		return await this.api.fetch(`/reports/${template.id}/template/delete`, {
			method: 'delete'
		});
	}

	async getComparisonTypes(): Promise<IComparisonType[]> {
		return await this.api.fetchJson(`reports/comparison-types`);
	}

	async hasCustomerReportDimensionFilters(customerId: string): Promise<boolean> {
		return await this.api.fetchJson(`reports/report-dimension-filters-exist/for-customer/${customerId}`);
	}

	async getUntargetedFinancialEntriesForScope(scopeId: string): Promise<IUntargetedFinancialEntryDataAM[]> {
		return await this.api.fetchJson(`reports/untargeted-financial-entries/for-scope/${scopeId}`);
	}

	async createReportFolder(customerId: string, folderName: string, isPrivate: boolean) {
		const folder: IReportFolderAIM = { name: folderName };
		return await this.api.fetch(`reports/folders/for-customer/${customerId}/add?isPrivate=${isPrivate}`, {
			method: 'post',
			body: json(folder)
		});
	}

	async moveReportToFolder(reportId: string, folderId: string, sortOrder: number) {
		return await this.api.fetch(`reports/${reportId}/move/${folderId}/to/${sortOrder}`, { method: 'post' });
	}

	async moveFolderToParentFolder(folderId: string, parentFolderId: string) {
		return await this.api.fetch(`reports/folders/${folderId}/move/${parentFolderId}`, { method: 'post' });
	}

	async renameFolder(folderId: string, newName: string) {
		const folder: IReportFolderAIM = { name: newName };
		return await this.api.fetch(`reports/folders/${folderId}/rename`, {
			method: 'post',
			body: json(folder)
		});
	}

	async deleteFolder(folderId: string) {
		return await this.api.fetch(`reports/folders/${folderId}`, { method: 'delete' });
	}

	async getCustomerWordTemplates(customerId: string): Promise<IWordTemplateAM[]> {
		const res = await this.api.fetch(`reports/${customerId}/word-templates`);
		const data = await res.json();
		return data;
	}

	async deleteWordTemplate(customerId: string, templateId: string): Promise<IWordTemplateAM> {
		const res = await this.api.fetch(`reports/${customerId}/word-templates/${templateId}`, { method: "delete" });
		const data = await res.json();
		return data;
	}
}

export interface IWordTemplateBaseAM {
	id: string;
	originalFileName: string;
}

export interface IWordTemplateAM extends IWordTemplateBaseAM {
	created: Date;
	createdBy: string;
}

export interface IReportAM {
	reportTemplateId: string;
	heading: string;
	headerCells: IReportHeaderCellAM[];
	rows: IReportRowAM[];
	costCalculationsAreExcluded: boolean;
	notification: string;
	financialPrecision: ReportFinancialPrecision;
}

export interface IReportHeaderCellAM {
	columnId: string;
	type: string;
	scopeName: string;
	costCenters: string[];
	municipality: string;
	title: string;
}

export interface IReportRowAM {
	title: string;
	identifier: string;
	hierarchyType: string;
}

export interface IReportValueRowAM extends IReportRowAM {
	unit: string;
	cells: IReportCellAM[];
	error: string;
	/** "Tarkenteet" row below an account row; sent only when true. Cannot be deleted in the editor. */
	isDimensionCombination?: boolean;
}

export interface IReportGroupRowAM extends IReportRowAM {
	rows: IReportRowAM[];
}

export interface IReportCellAM {
	value: number;
	unitOverride: string;
}

export interface IReportLinkResultAM {
	privateLinks: ReportLinkAM[];
	commonLinks: ReportLinkAM[];
}

export class ReportLinkAM implements IDraggableHandler {
	url: string;
	name: string;
	reportId: string;
	private: boolean;
	isFolder: boolean;
	hasChildItems: boolean;
	isOpen: boolean;
	isVisible: boolean;
	parentFolderId: string;
	created: string;
	createdBy: string;
	modified: string;
	modifiedBy: string;
	hierarchyLevel: number;
	isDragged: boolean;
	isBeingEdited: boolean;
	sortOrder: number | null;

	onDragStart(event, data: ReportLinkAM) {
		if (event.stopPropagation) {
			event.stopPropagation();
		}

		if (!this.reportId) {
			return false;
		}
		
		this.isDragged = true;
		event.dataTransfer.effectAllowed = 'move';
		const json = JSON.stringify(data);
		event.dataTransfer.setData('application/json', json);
	}

	onDragEnd(event, data: any): void {
		this.isDragged = false;
	}
}

export interface IUntargetedFinancialEntryDataAM {
	entries: IUntargetedFinancialEntryGroupAM[];
	commonRuleCount: number;
}

export interface IUntargetedFinancialEntryGroupAM {
	costCenterIdentifier: string;
	costCenterName: string;
	serviceCode: string;
	costCenterHasServiceTargetings: boolean;
	municipality: string;
	amountSum: number;
	lastCreated: Date;
}

export interface IUntargetedFinancialEntryMunicipalityAllocationRuleAM {
	ruleId: string;
	allocationType: string;
	allocationArguments: string;
}

interface IReportFolderAIM {
	name: string;
}
